import { SourceDataVerificationPermission } from '@/api/dtos';
import ConfigurationSummaryList from '@/components/data/configuration/summary/ConfigurationSummaryList';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationSummaries from '@/hooks/data/useConfigurationSummaries';
import usePermissions from '@/hooks/utility/usePermissions';
import { faDatabase, faGlobeOceania, faPlus } from '@fortawesome/duotone-light-svg-icons';
import { faCheckCircle } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Grid, IconButton, Paper, Snackbar, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { Form, IFormLabel, IValidationError, InputOnlyField, Text, BasicAlert } from '@ngt/forms';
import { IFormSubmit, SubmitOn } from '@ngt/forms-core';
import { FC, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams, useLocation, Navigate } from 'react-router-dom';


const StickySearch = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    position: 'sticky',
    top: 48 /* header height */ + 24,
    
    '@media (min-width: 600px)': {
        top: 64 /* header height */ + 24,
    }
}));

const GridSearchColumn = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        order: 2
    }
}));

const Title = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center'
}));

const ButtonBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    marginLeft: 'auto',
    marginRight: theme.spacing(2)
}));

const labels: Array<Partial<IFormLabel>> = [
    {
        name: 'search',
        label: 'Search'
    }
]

interface IForm {
    search?: string;
}

const initialState: IForm = {
    search: undefined
}



const Configurations: FC = () => {
    


    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Configurations',
                link: `/`,
                icon: faDatabase
            },
        ];
    }, []);


    const { trialCode } = useParams();
    const { state } = useLocation();
    const [ showSuccess, setShowSuccess ] = useState(() => { return (state as any)?.success ?? false; });
    const theme = useTheme();
    const handleSnackClose = () => {
        setShowSuccess(false);
    };


    const { data: trial } = useContext(TrialContext);
    const { data: configurations, loading: configurationsLoading, validating: configurationsUpdating } = useConfigurationSummaries(trialCode!, { revalidateOnFocus: false });

    const loadingData = configurationsLoading ;
    const updating = !loadingData && (configurationsUpdating);
    
    const initialValue = useMemo(() => {
        return {...initialState};
    }, [trialCode])

    const [formValue, setFormValue] = useState(initialValue);

    useEffect(() => {
        setFormValue({...initialState})
    }, [trialCode])

    const onSubmit: IFormSubmit<IForm, IValidationError> = useCallback(async ( state: any ) => {
        setFormValue(state.value!);

        return {}
    }, [setFormValue])

    const { 
        data: [
            hasEditConfiguration
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.EditConfiguration
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );

    const loading = loadingData || permissionsLoading || permissionsValidating;

    if (!hasEditConfiguration && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }
    
    return (
        <>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <PageTitle title={`Configurations${TITLE_SEPARATOR}${trial?.name}`} />
            <Title 
                variant="h1" 
                gutterBottom
                color={theme.palette.secondary.main}
            >
                Configurations
                <Tooltip title="New Configuration">
                    <ButtonBox>
                        <IconButton component={Link} to={`/${trialCode}/configurations/new`}>
                            <FontAwesomeIcon icon={faPlus} />
                        </IconButton>
                    </ButtonBox>
                </Tooltip>
                <Tooltip title="Trial Configuration">
                        <IconButton component={Link} to={`/${trialCode}/configurations/trial`}>
                            <FontAwesomeIcon icon={faGlobeOceania} />
                        </IconButton>
                </Tooltip>
            </Title>
            <Grid 
                container
                spacing={2}
            >
                <GridSearchColumn 
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={4}
                    xl={3}
                >
                    <StickySearch>
                        <Form
                            initialValue={initialValue}
                            labels={labels}
                            debug={import.meta.env.DEV}
                            submitOn={SubmitOn.onChange}
                            onSubmit={onSubmit}
                        >
                            <InputOnlyField 
                                propertyPath="search"
                                component={Text}
                                margin="normal"
                            />
                        </Form>
                    </StickySearch>
                </GridSearchColumn>
                <Grid 
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={8}
                    xl={9}
                >
                    <Paper>
                        <ConfigurationSummaryList
                            search={formValue.search}
                            summaries={configurations}
                            trial={trial}
                            updating={updating}
                            loading={loading}
                        />
                    </Paper>
                </Grid>
            </Grid>
            <Snackbar
                open={showSuccess}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right'}}
                onClose={handleSnackClose}
            >
                <div>
                    <BasicAlert 
                        severity="success"
                        icon={faCheckCircle}
                        title="Successfully Saved Settings"
                        description={
                            `The monitoring visit settings for the trial have been successfully saved.`
                        }
                    />
                </div>
            </Snackbar>
        </>
    );
}

export default Configurations;